import { useEffect, useMemo, useRef, useState } from 'react';
import { useBeforeUnload, useBlocker } from 'react-router-dom';
import { useIntl } from '@/core/i18n/use-intl';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';

type UseUnsavedChangesGuardOptions = {
  when: boolean;
  onDiscard: () => void | Promise<void>;
};

export function useUnsavedChangesGuard({ when, onDiscard }: UseUnsavedChangesGuardOptions) {
  const { t } = useIntl();
  const whenRef = useRef(when);
  whenRef.current = when;
  const bypassRef = useRef(false);

  const blocker = useBlocker(() => whenRef.current && !bypassRef.current);
  const [open, setOpen] = useState(false);
  const closeIntentRef = useRef<'none' | 'stay' | 'discard'>('none');

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setOpen(true);
    }
  }, [blocker.state]);

  useBeforeUnload(
    useMemo(
      () => (event: BeforeUnloadEvent) => {
        if (!when) return;
        event.preventDefault();
        event.returnValue = '';
      },
      [when]
    )
  );

  const stay = () => {
    blocker.reset?.();
    setOpen(false);
  };

  const discardAndProceed = async () => {
    await onDiscard();
    blocker.proceed?.();
    setOpen(false);
  };

  const dialog = (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setOpen(true);
          return;
        }

        const intent = closeIntentRef.current;
        closeIntentRef.current = 'none';

        if (intent === 'discard') {
          void discardAndProceed();
          return;
        }

        stay();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('common.unsavedChangesTitle')}</AlertDialogTitle>
          <AlertDialogDescription>{t('common.unsavedChangesDescription')}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onPointerDownCapture={() => {
              closeIntentRef.current = 'stay';
            }}
          >
            {t('common.stay')}
          </AlertDialogCancel>
          <AlertDialogAction
            onPointerDownCapture={() => {
              closeIntentRef.current = 'discard';
            }}
          >
            {t('common.discard')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  const allowNextNavigation = () => {
    bypassRef.current = true;
    setTimeout(() => {
      bypassRef.current = false;
    }, 0);
  };

  return { dialog, isBlocked: blocker.state === 'blocked', allowNextNavigation };
}
